import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@components/ui/table';
import { cn } from '@lib/utils';

export interface TableColumn {
  id: string;
  label: string;
}

export interface TableRowData {
  id: string;
  [key: string]: unknown;
}

interface DataTableProps {
  columns: TableColumn[];
  rows: TableRowData[];
  onRowSelect?: (selectedIds: string[]) => void;
  rowsPerPageOptions?: number[];
  pagination?: boolean;
  noDataFoundText?: string;
  headBackgroundColor?: string;
  onRowClick?: (row: TableRowData) => void;
  checkbox?: boolean;
  customRowStyling?: string;
  externalPagination?: boolean;
  page?: number;
  totalPages?: number;
  totalCount?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

const TableCheckbox = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }
>(({ indeterminate, className, ...props }, ref) => {
  const innerRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current!);
  React.useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = indeterminate ?? false;
    }
  }, [indeterminate]);
  return (
    <input
      type="checkbox"
      ref={innerRef}
      role="checkbox"
      className={cn(
        'h-4 w-4 rounded border border-[#A7A7A7] bg-transparent accent-[#156BE7] cursor-pointer',
        className,
      )}
      {...props}
    />
  );
});
TableCheckbox.displayName = 'TableCheckbox';

const NavButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  ...props
}) => (
  <button
    className={cn(
      'p-1.5 rounded border border-[#E5E5E5] bg-white hover:bg-[#f5f5f5] disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
      className,
    )}
    {...props}
  />
);

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  onRowSelect,
  rowsPerPageOptions = [5, 10, 25],
  pagination = true,
  noDataFoundText,
  headBackgroundColor,
  onRowClick,
  checkbox = true,
  customRowStyling,
  externalPagination = false,
  page: externalPage = 1,
  totalPages: externalTotalPages = 0,
  totalCount: externalTotalCount = 0,
  rowsPerPage: externalRowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<string[]>([]);
  const [internalPage, setInternalPage] = React.useState(1);
  const [internalRowsPerPage, setInternalRowsPerPage] = React.useState(
    rowsPerPageOptions[1] ?? rowsPerPageOptions[0],
  );

  const currentPage = externalPagination ? externalPage : internalPage;
  const currentRowsPerPage = externalPagination ? externalRowsPerPage : internalRowsPerPage;
  const currentTotalPages = externalPagination
    ? externalTotalPages
    : Math.ceil(rows.length / currentRowsPerPage);
  const currentTotalCount = externalPagination ? externalTotalCount : rows.length;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = e.target.checked ? rows.map((r) => r.id) : [];
    setSelected(newSelected);
    onRowSelect?.(newSelected);
  };

  const handleRowClick = (row: TableRowData) => {
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    const idx = selected.indexOf(row.id);
    const newSelected = idx === -1 ? [...selected, row.id] : selected.filter((id) => id !== row.id);
    setSelected(newSelected);
    onRowSelect?.(newSelected);
  };

  const goToPage = (page: number) => {
    if (externalPagination && onPageChange) {
      onPageChange(page);
    } else {
      setInternalPage(page);
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = Number(e.target.value);
    if (externalPagination && onRowsPerPageChange) {
      onRowsPerPageChange(val);
    } else {
      setInternalRowsPerPage(val);
      setInternalPage(1);
    }
  };

  const displayRows: TableRowData[] = externalPagination
    ? rows
    : rows.slice((currentPage - 1) * currentRowsPerPage, currentPage * currentRowsPerPage);

  return (
    <>
      <div className="rounded-xl overflow-hidden border border-[#e5e7eb]">
        <Table>
          <TableHeader>
            <TableRow
              className="bg-[#F5F5F5] hover:bg-[#F5F5F5] !border-[#E5E5E5]"
              style={headBackgroundColor ? { backgroundColor: headBackgroundColor } : undefined}
            >
              {checkbox && (
                <TableHead className="w-12 px-3">
                  <TableCheckbox
                    indeterminate={selected.length > 0 && selected.length < rows.length}
                    checked={rows.length > 0 && selected.length === rows.length}
                    onChange={handleSelectAll}
                    aria-label="select all rows"
                  />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead key={col.id} className="primary-font-medium text-[13px] !text-[#111827]">
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.length > 0 ? (
              displayRows.map((row) => {
                const isSelected = selected.includes(row.id);
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    data-state={isSelected ? 'selected' : undefined}
                    className={cn(
                      'bg-white hover:bg-[#f9fafb] text-[#111827] !border-[#E5E5E5] cursor-pointer',
                      customRowStyling,
                    )}
                  >
                    {checkbox && (
                      <TableCell className="w-12 px-3 !border-[#E5E5E5]">
                        <TableCheckbox
                          checked={isSelected}
                          onChange={() => handleRowClick(row)}
                          aria-label={`select row ${row.id}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id} className="!border-[#E5E5E5] text-[#111827]">
                        {row[col.id] as React.ReactNode}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length + (checkbox ? 1 : 0)}
                  className="text-center py-10 text-[#9CA3AF] text-[14px] border-0"
                >
                  {noDataFoundText || t('table.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex flex-wrap items-center gap-y-2 px-4 py-3 text-sm text-[#6B7280]">
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap w-full sm:w-auto justify-center sm:justify-end sm:ml-auto">
            <div className="flex items-center gap-1.5">
              <span className="whitespace-nowrap text-xs sm:text-sm">{t('table.rowsPerPage')}</span>
              <select
                value={currentRowsPerPage}
                onChange={handleRowsPerPageChange}
                className="rounded px-1.5 py-1 text-xs sm:text-sm bg-white border border-[#E5E5E5] text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#156BE7] cursor-pointer"
              >
                {rowsPerPageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <span className="whitespace-nowrap text-xs sm:text-sm">
              {t('common.pageOf', { current: currentPage, total: currentTotalPages || 1 })}
            </span>

            <div className="flex items-center gap-1">
              <NavButton
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                aria-label={t('table.firstPage')}
              >
                <ChevronsLeft size={14} />
              </NavButton>
              <NavButton
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label={t('table.previousPage')}
              >
                <ChevronLeft size={14} />
              </NavButton>
              <NavButton
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= currentTotalPages}
                aria-label={t('table.nextPage')}
              >
                <ChevronRight size={14} />
              </NavButton>
              <NavButton
                onClick={() => goToPage(currentTotalPages)}
                disabled={currentPage >= currentTotalPages}
                aria-label={t('table.lastPage')}
              >
                <ChevronsRight size={14} />
              </NavButton>
            </div>

            <span className="whitespace-nowrap text-xs sm:text-sm">
              {t('table.rowsTotal', { count: currentTotalCount })}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default DataTable;
